/**
 * Shared matching utilities for Association Template benefit/plan name comparison.
 */

/** Splits a name into a set of lowercase alphanumeric tokens. */
export const tokenizeName = (name: string): Set<string> =>
    new Set((name ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean));

/**
 * Benefit name match: the smaller token set must be a subset of the larger.
 * e.g. "Plan 1" matches "Medical - Plan 1" because {plan,1} ⊆ {medical,plan,1}.
 */
export const benefitNamesMatch = (a: string, b: string): boolean => {
    const tokA = tokenizeName(a);
    const tokB = tokenizeName(b);
    const [smaller, larger] = tokA.size <= tokB.size ? [tokA, tokB] : [tokB, tokA];
    return smaller.size > 0 && [...smaller].every(t => larger.has(t));
};

/**
 * Plan name match: same token-subset rule but ALSO requires identical numeric tokens.
 * Prevents cross-plan picks: "Plan 11" vs "Plan 1" → numeric {11} ≠ {1} → no match.
 */
export const planNamesMatch = (a: string, b: string): boolean => {
    const tokA = tokenizeName(a);
    const tokB = tokenizeName(b);
    const numA = new Set([...tokA].filter(t => /^\d+$/.test(t)));
    const numB = new Set([...tokB].filter(t => /^\d+$/.test(t)));
    if (numA.size !== numB.size) return false;
    if (![...numA].every(n => numB.has(n))) return false;
    const [smaller, larger] = tokA.size <= tokB.size ? [tokA, tokB] : [tokB, tokA];
    return [...smaller].every(t => larger.has(t));
};

/**
 * Safe category ID equality: returns true only when both IDs are non-empty
 * and equal. Prevents '' === '' false matches for uncategorized (Ancillary) benefits.
 */
export const categoriesMatch = (a: string | null | undefined, b: string | null | undefined): boolean => {
    const catA = String(a ?? '').trim();
    const catB = String(b ?? '').trim();
    return catA !== '' && catB !== '' && catA === catB;
};
